import { useEffect } from 'react';
import { UseMutationResult } from 'react-query';

import { Address } from '@/domain/address/models/address';
import { Modal } from '@/shared/components/Modal';
import { useAddressContext } from '@/shared/contexts/AddressContext';
import { getCookies } from '@/shared/utils/cookie';

import { AddAddress } from './steps/Create';
import { EditAddress } from './steps/Edit';
import { Empty } from './steps/Empty';
import { SearchAddress } from './steps/Search';
import * as S from './styles';

interface ModalAddressProps {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  handleOnSelectUserAddress: (addressToSelect: Address) => void;
  handleOnEditAddress: (id: string) => void;
  isOpenDialogDeleteAddress: boolean;
  onOpenDialogDeleteConfirmation: (id: string) => void;
  onCloseDialogDeleteConfirmation: () => void;
  deleteAddressMutation: UseMutationResult<Address[], unknown, void, unknown>;
  addressConfirmationMutation: UseMutationResult<
    Address[],
    unknown,
    Address,
    unknown
  >;
}

export const ModalAddress: React.FC<ModalAddressProps> = ({
  isOpen,
  onOpen,
  onClose,
  handleOnSelectUserAddress,
  handleOnEditAddress,
  isOpenDialogDeleteAddress,
  onOpenDialogDeleteConfirmation,
  onCloseDialogDeleteConfirmation,
  addressConfirmationMutation,
  deleteAddressMutation,
}) => {
  const {
    userAddresses,
    currentStep,
    setCurrentStep,
    clearState,
    onReadAddresses,
  } = useAddressContext();

  useEffect(() => {
    if (!isOpen) return;

    addressConfirmationMutation.reset();
    deleteAddressMutation.reset();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const getAdressCookies = () => {
    const {
      'fapp.address': address,
      'fapp.accessToken': acessToken,
      'fapp.refreshToken': refreshToken,
    } = getCookies();

    return { address, acessToken, refreshToken };
  };

  useEffect(() => {
    (async () => {
      const { address, acessToken, refreshToken } = getAdressCookies();

      if (!(acessToken && refreshToken)) {
        clearState();
        setCurrentStep('search');
        return;
      }

      if (acessToken && refreshToken) {
        const data = await onReadAddresses();

        clearState();

        if (!(address && data.length)) {
          setCurrentStep('empty');
          onOpen();
          return;
        }

        if (address && !data.length) {
          setCurrentStep('empty');
          return;
        }

        if (address && data.length) setCurrentStep('create');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isOpen === false) {
      const { address, acessToken, refreshToken } = getAdressCookies();

      clearState();

      if (acessToken && refreshToken) {
        if (
          (address && !userAddresses.length) ||
          (!address && !userAddresses.length)
        ) {
          setCurrentStep('empty');
          return;
        }

        if (userAddresses.length) {
          setCurrentStep('create');
          return;
        }
      }

      setCurrentStep('search');
    }
  }, [isOpen, userAddresses.length, setCurrentStep, clearState]);

  const components = {
    empty: <Empty />,
    create: (
      <AddAddress
        handleOnSelectUserAddress={handleOnSelectUserAddress}
        handleOnEditAddress={handleOnEditAddress}
        isOpenDialogDeleteAddress={isOpenDialogDeleteAddress}
        onOpenDialogDeleteConfirmation={onOpenDialogDeleteConfirmation}
        onCloseDialogDeleteConfirmation={onCloseDialogDeleteConfirmation}
        deleteAddressMutation={deleteAddressMutation}
      />
    ),
    search: <SearchAddress />,
    edit: (
      <EditAddress addressConfirmationMutation={addressConfirmationMutation} />
    ),
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <S.Wrapper>{components[currentStep]}</S.Wrapper>
    </Modal>
  );
};
